<template>
  <UCard
    :ui="publicCard"
    class="fixed left-0 right-0 bottom-0 z-50 lg:z-0 lg:mt-8 lg:relative lg:left-auto lg:right-auto lg:bottom-auto"
  >
    <UForm :schema="schema" :state="state" class="grid grid-cols-3 gap-3">
      <TDateInput
        v-model:dates="state.dates"
        :item-id="props.item.id"
        :message="calendarUpdate"
        :show-icon-mobile="false"
        :verify-calendar="props.item.hasVerifiedCalendar"
      />
      <UInput
        v-model="state.customers"
        data-test="customers-input"
        type="number"
        min="0"
        class="grow col-span-1 lg:col-span-3"
        :placeholder="t('views.cottage.aside.customers')"
        :aria-label="t('views.cottage.aside.customers')"
        :title="t('views.cottage.aside.customers-title')"
        size="xl"
        autocomplete="on"
      />
      <div class="col-span-3 flex gap-x-2">
        <UButton
          data-test="cottage-aside-contact-button"
          color="neutral"
          variant="subtle"
          icon="i-heroicons-envelope"
          :aria-label="t('views.cottage.aside.sendMessage')"
          size="xl"
          :to="goToCottageContact"
          rel="nofollow"
          external
        />
        <UButton
          v-if="props.item.salesService.isAllowedToShowWhatsapp"
          data-test="cottage-aside-whatsapp-button"
          color="neutral"
          variant="subtle"
          icon="i-ic-baseline-whatsapp"
          :aria-label="t('views.cottage.aside.sendMessage')"
          size="xl"
          :to="goToCottageWhatsapp"
          rel="nofollow"
          external
          class="hidden"
        />
        <UButton
          data-test="submit-button"
          type="submit"
          class="grow flex justify-center"
          size="xl"
          :loading="isLoading"
          @click.prevent="onSubmit"
        >
          {{ t('views.cottage.aside.toPrices') }}
        </UButton>
      </div>
    </UForm>
  </UCard>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { datadogLogs } from '@datadog/browser-logs'
import type { DatePickerRangeObject } from '@angelblanco/v-calendar/dist/types/src/use/datePicker.js'
import type { CottagePage } from '../entities/CottagePage'
import { publicCard } from '~~/modules/design-system/utils/nuxt-ui-config/components/card'
import { formatCottageCalendarUpdate } from '../utils/formatCottageCalendarUpdate'
import { useSavedDatesRuralityCookie } from '../composables/useSavedDatesRuralityCookie'
import type { SavedDatesRuralityCookie } from '../entities/SavedDatesRuralityCookie'

const route = useRoute()
const { getSavedDates, setSavedDates } = useSavedDatesRuralityCookie()

type ItemType = Pick<
  CottagePage,
  | 'name'
  | 'province'
  | 'slug'
  | 'id'
  | 'calendarUpdate'
  | 'salesService'
  | 'hasVerifiedCalendar'
>

interface Props {
  item: ItemType
}

const props = defineProps<Props>()

const schema = z.object({
  dates: z.custom<DatePickerRangeObject>().nullable(),
  customers: z.number().int(),
})

type SchemaType = z.infer<typeof schema>

interface State {
  dates: DatePickerRangeObject | null
  customers: SchemaType['customers'] | undefined
}

const state = reactive<State>({
  dates: null,
  customers: undefined,
})

const isLoading = ref(false)

const calendarUpdate = computed(() => {
  if (props.item.hasVerifiedCalendar) {
    return t('views.cottage.aside.verifiedCalendar')
  }
  return props.item?.calendarUpdate
    ? formatCottageCalendarUpdate(props.item.calendarUpdate.daysSinceLastUpdate)
    : undefined
})

const setCookieDates = () => {
  if (!state.dates) return
  const data: SavedDatesRuralityCookie = {
    dateIn: toRuralityDate().legacyDate(state.dates.start as Date),
    dateOut: toRuralityDate().legacyDate(state.dates.end as Date),
  }
  if (state.customers) {
    data.capacity = state.customers.toString()
  }
  setSavedDates(data)
}

const goToCottageContact = routes().cottageContact({
  slug: route.params.slug as string,
  province: route.params.province as string,
})

const goToCottageWhatsapp = routes().cottageContactWhatsApp({
  slug: route.params.slug as string,
  province: route.params.province as string,
})

const onSubmit = () => {
  if (schema.safeParse(state).error || state.dates === null) {
    alert(t('views.cottage.aside.noData'))
  } else {
    isLoading.value = true
    datadogLogs.logger.info('NuxtFunnelLogs: Step 1 - Go To Prices Page')

    setCookieDates()
    navigateTo(
      routes().prices({
        slug: props.item.slug,
        province: props.item.province.slug,
        dateIn: toRuralityDate().legacyDate(state.dates.start as Date),
        dateOut: toRuralityDate().legacyDate(state.dates.end as Date),
        capacity: state.customers?.toString(),
      }),
    )

    isLoading.value = false
  }
}

onMounted(() => {
  if (!import.meta.client) return

  const initialSavedDates = getSavedDates()

  if ((route.query.dateIn && route.query.dateOut) || route.query.capacity) {
    if (route.query.dateIn && route.query.dateOut) {
      state.dates = {
        start: toRuralityDate().parseLegacyDate(route.query.dateIn as string),
        end: toRuralityDate().parseLegacyDate(route.query.dateOut as string),
      }
    }
    if (route.query.capacity) {
      state.customers = parseInt(route.query.capacity as string)
    }
    setCookieDates()
  } else if (initialSavedDates) {
    if (initialSavedDates.dateIn && initialSavedDates.dateOut) {
      state.dates = {
        start: toRuralityDate().parseLegacyDate(initialSavedDates.dateIn),
        end: toRuralityDate().parseLegacyDate(initialSavedDates.dateOut),
      }
    }
    if (initialSavedDates.capacity) {
      state.customers = parseInt(initialSavedDates.capacity)
    }
  }
})
</script>
